import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Maximize2, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { INSTAGRAM, PROJECTS, requestQuote } from "@/lib/site";
import { Reveal, Overline } from "./Reveal";

const Frame = ({ p, i, onOpen }) => (
  <motion.button type="button" data-testid={`project-item-${i}`} onClick={() => onOpen(p)}
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative chamfer-sm overflow-hidden bg-[#0e1116] text-left ${p.frame}`}
    aria-label={`View ${p.title}`}>
    <img src={p.src} alt={p.title} loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.04]" />
    {p.plain ? (
      <span className="absolute top-3 right-3 grid place-items-center h-10 w-10 rounded-full bg-[#0e1116]/70 backdrop-blur text-white transition-colors group-hover:bg-[#e8c36a] group-hover:text-[#0e1116]">
        <Maximize2 className="h-4 w-4" />
      </span>
    ) : (<>
    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0e1116]/85 to-transparent opacity-90" />
    <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 flex items-end justify-between gap-4">
      <div className="min-w-0">
        <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#e8c36a]">{String(i + 1).padStart(2, "0")} · {p.tag}</p>
        <p className="font-display text-base sm:text-xl font-semibold text-white mt-1">{p.title}</p>
      </div>
      <span className="shrink-0 grid place-items-center h-10 w-10 rounded-full bg-white/15 backdrop-blur text-white transition-colors group-hover:bg-[#e8c36a] group-hover:text-[#0e1116]">
        <Maximize2 className="h-4 w-4" />
      </span>
    </div>
    </>)}
  </motion.button>
);

const NextTile = () => (
  <Reveal className="lg:col-span-4 bg-gold-grad p-6 sm:p-8 flex flex-col justify-between gap-8 min-h-[260px]">
    <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#2a1f08]">Your project next</p>
    <div>
      <h3 className="font-display text-3xl font-bold tracking-tight text-[#0e1116]">Want something like this?</h3>
      <p className="mt-3 text-[#2a1f08]">Share a photo or size and we'll quote it.</p>
      <button data-testid="projects-quote-btn" onClick={() => requestQuote("")}
        className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#0e1116] text-white font-semibold hover:bg-black transition-colors">
        Request a quotation <ArrowUpRight className="h-4 w-4 text-[#e8c36a]" />
      </button>
    </div>
  </Reveal>
);

export const Projects = () => {
  const [open, setOpen] = useState(null);
  return (
    <section id="projects" data-testid="projects-section" className="py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <Reveal>
            <Overline>Completed projects</Overline>
            <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.98]">
              Work from<br /><span className="text-silver-grad">our floor.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="max-w-md">
            <p className="text-base md:text-lg text-slate-700 leading-relaxed">
              Finished teak work from the TSR Industry workshop. Tap any photo to see it in full, and follow our latest jobs on Instagram.
            </p>
            <a data-testid="projects-instagram-link" href={INSTAGRAM} target="_blank" rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-400 font-semibold hover:bg-[#0e1116] hover:text-white transition-colors">
              <Instagram className="h-4 w-4" /> @tsr_industry
            </a>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">
          {PROJECTS.map((p, i) => <Frame key={p.src} p={p} i={i} onOpen={setOpen} />)}
          <NextTile />
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid="project-lightbox" className="max-w-[min(1200px,94vw)] p-2 sm:p-3 bg-[#0e1116] border-[#0e1116] text-white">
          <DialogTitle className="sr-only">{open?.title}</DialogTitle>
          {open && <img src={open.src} alt={open.title} className="w-full max-h-[80vh] object-contain" />}
          <p className="px-2 pb-1 text-sm text-slate-200">{open?.title}</p>
        </DialogContent>
      </Dialog>
    </section>
  );
};
